/**
 * `<CompareView/>` — compare two dates (issue #765, B3.5): the "then vs now"
 * diff of the plan. The reflection / progress-report view.
 *
 * Faithful to `B3-5-compare-dates.svg`:
 *   - chrome: two pickers (a "then" date ⇄ a "now" / second date), with a
 *     "⇆" between them; the right side defaults to "now";
 *   - LEFT — the diff tree: every node that was **added**, **removed**, or
 *     **changed** between the two points, grouped by fate, with a legend
 *     (+ added · ~ changed · ⊖ removed);
 *   - RIGHT — the focal "WHAT CHANGED" summary: Added (n) / Changed (n) /
 *     Removed (n), each listing the nodes, with a "◐ → ●" state-move badge on
 *     changed rows. The counts are the headline.
 *
 * Two selection modes (the brief: "two dates, or two snapshots"): the picker
 * accepts a date on each side; a future caller may pass snapshot ids. We keep
 * the selection as a `CompareSelection` and forward it to `useTaskCompare`.
 *
 * One diff-rendering core: both the left tree-list and the right summary read
 * the SAME `CompareResult` and the SAME `describeChange` phrasing, so the two
 * panels can never disagree. The diff is about THE PLAN's structure.
 *
 * Editorial revamp (#1043, Wave 2): theme-aware via `useMapTheme()`. The flag
 * stays OFF for every team (default LEGACY) until the whole revamp lands, so:
 *   - LEGACY (flag off): the shipped look — emerald added / green changed /
 *     red-dashed removed tints, the indigo "now" pill. Byte-identical to today.
 *   - EDITORIAL (flag on): the wireframe `V_B35` look — mono `<date> ▾ ⇄ now ▾`
 *     date chips, a serif "Compare" title, and the warm diff legend: **+ added**
 *     = sage `--st-done-bg` dashed, **~ changed** = warm gold `#FFF6E0`, **⊖
 *     removed** = neutral `--bg-3`; a `c.bgRecess` right rail with a mono "WHAT
 *     CHANGED" label, tinted group rows, and state transitions shown as
 *     `<dot> → <dot>` via `theme.stateStyle`. Amber-never-red holds. RE-SKIN,
 *     not rebuild: every `data-testid`, copy, and the compare behaviour are
 *     unchanged; the diff `describeChange` logic is untouched (styling only).
 *
 * Graceful degradation: a not-yet-deployed endpoint (`isError`) → a calm
 * unavailable state; before both sides are picked → a "pick two points"
 * prompt; an all-zero diff → a calm "nothing changed" summary. Never a crash.
 *
 * Brand firewall (§A9): the diff is nodes added/removed/changed — NEVER a
 * per-person tally. `describeChange` records WHAT moved on a node, never WHO.
 * Every count is about NODES (the plan), never people.
 *
 * Isolation: taskmap-local imports only — no scheduler coupling.
 */

import { useMemo, useState } from "react";

import {
  type CompareResult,
  type CompareSelection,
  type NodeChange,
  useTaskCompare,
} from "../api/useTaskCompare";
import type { TaskMember } from "../members/useTaskTeamMembers";
import { formatDayLabel } from "../rewind/rewindTimeline";
import { useMapTheme } from "../theme/MapThemeContext";
import type { MapTheme } from "../theme/tokens";
import { PALETTE } from "../tree/palette";
import { type TaskState, taskStateStyle } from "../tree/state";
import { describeChange, stateArrowGlyph } from "./describeChange";

// Legacy diff palette (byte-identical to the shipped look).
const ADDED_BG = "#D1FAE5";
const ADDED_INK = "#059669";
const CHANGED_BG = "#F0FDF4";
const REMOVED_BG = "#FEF2F2";
const REMOVED_BORDER = "#EF4444";
const NEUTRAL_INK = "#9CA3AF";
const INDIGO = "#4F46E5";

// Editorial diff palette (`V_B35` / `tarii.css`): added = sage done-bg dashed,
// changed = warm gold, removed = neutral `--bg-3`. Amber-never-red holds.
const ED_ADDED_BG = "#DDE9D9"; // --st-done-bg
const ED_ADDED_INK = "#5E8E5C"; // --st-done (sage)
const ED_CHANGED_BG = "#FFF6E0"; // warm gold wash
const ED_CHANGED_BORDER = "#B0934A"; // warm gold rule
const ED_CHANGED_INK = "#8E6A1A"; // warm gold ink
const ED_REMOVED_BG = "#ECE6D6"; // --bg-3 (neutral)

/** The diff colour tokens a row reads, resolved per theme. */
interface DiffTones {
  addedBg: string;
  addedInk: string;
  changedBg: string;
  changedBorder: string;
  changedInk: string;
  removedBg: string;
  removedBorder: string;
  removedInk: string;
  /** The fallback (non-state) badge tint — indigo legacy / teal editorial. */
  badgeBg: string;
  badgeInk: string;
}

function diffTones(theme: MapTheme): DiffTones {
  const c = theme.palette;
  return theme.mode === "editorial"
    ? {
        addedBg: ED_ADDED_BG,
        addedInk: ED_ADDED_INK,
        changedBg: ED_CHANGED_BG,
        changedBorder: ED_CHANGED_BORDER,
        changedInk: ED_CHANGED_INK,
        removedBg: ED_REMOVED_BG,
        removedBorder: c.rule2,
        removedInk: c.muted,
        badgeBg: c.tealSoft,
        badgeInk: c.tealInk,
      }
    : {
        addedBg: ADDED_BG,
        addedInk: ADDED_INK,
        changedBg: CHANGED_BG,
        changedBorder: ADDED_INK,
        changedInk: ADDED_INK,
        removedBg: REMOVED_BG,
        removedBorder: REMOVED_BORDER,
        removedInk: NEUTRAL_INK,
        badgeBg: "#EEF2FF",
        badgeInk: INDIGO,
      };
}

/** "YYYY-MM-DD" date-input value → an end-of-day ISO instant (or undefined). */
function dateToISO(date: string): string | undefined {
  return date ? new Date(`${date}T23:59:59.999`).toISOString() : undefined;
}

/** A calm label for the resolved instant ("May 12" / "now"). */
function instantLabel(iso: string | null | undefined, isNow: boolean): string {
  if (isNow) return "now";
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : formatDayLabel(d);
}

/** A small state pill ("Done") tinted with its `TASK_STATES` colour. */
function StatePill({ state }: { state: TaskState }) {
  const style = taskStateStyle(state);
  return (
    <span
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold"
      style={{ backgroundColor: style.chipBg, color: style.fg }}
    >
      <span aria-hidden>{style.glyph}</span>
      {style.label}
    </span>
  );
}

/** The state-move badge ("Not started → Done") on a changed row. */
function ChangeBadges({ change, tones }: { change: NodeChange; tones: DiffTones }) {
  const theme = useMapTheme();
  const stateStyle = theme.stateStyle;
  const badges = describeChange(change);
  if (badges.length === 0) return null;
  return (
    <span className="flex flex-wrap items-center gap-1" data-testid="change-badges">
      {badges.map((badge) => (
        <span
          key={badge.text}
          className="rounded px-1.5 py-0.5 text-[10px] font-semibold"
          style={{
            backgroundColor: badge.toState ? stateStyle(badge.toState).chipBg : tones.badgeBg,
            color: badge.toState ? stateStyle(badge.toState).fg : tones.badgeInk,
          }}
        >
          {badge.text}
        </span>
      ))}
    </span>
  );
}

/**
 * The editorial `<dot> → <dot>` state-transition glyph (`V_B35` right rail),
 * coloured per the theme's `stateStyle`. Only renders on a state move; other
 * change dimensions still surface via `<ChangeBadges/>`.
 */
function StateTransitionDots({ change }: { change: NodeChange }) {
  const theme = useMapTheme();
  if (!change.state_changed) return null;
  const fromState = isTaskState(change.state_from) ? change.state_from : null;
  const toState = isTaskState(change.state_to) ? change.state_to : null;
  const glyph = stateArrowGlyph(change) ?? "";
  return (
    <span
      data-testid="state-transition-dots"
      className="ml-auto inline-flex items-center gap-1 text-[11px]"
      style={{ fontFamily: theme.fonts.mono, color: theme.palette.muted }}
      aria-label={glyph}
    >
      <span
        aria-hidden
        className="inline-block h-2 w-2 rounded-full"
        style={{
          backgroundColor: fromState ? theme.stateStyle(fromState).dot : theme.palette.faint,
        }}
      />
      →
      <span
        aria-hidden
        className="inline-block h-2 w-2 rounded-full"
        style={{ backgroundColor: toState ? theme.stateStyle(toState).dot : theme.palette.faint }}
      />
    </span>
  );
}

function isTaskState(v: unknown): v is TaskState {
  return v === "planned" || v === "in_progress" || v === "blocked" || v === "done";
}

export interface CompareViewProps {
  teamId: string | undefined;
  /** The team's roster (kept for parity with the other temporal surfaces). */
  members: TaskMember[];
  /** Return to the live editor. */
  onExit: () => void;
  /** "Now" — overridable for deterministic tests; defaults to the live instant. */
  now?: Date;
}

export function CompareView(props: CompareViewProps) {
  const { teamId, onExit, now } = props;

  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  // Freeze "now" once for the surface's life so the "now" side of a comparison
  // is a stable instant — otherwise a fresh `new Date()` per render would churn
  // the query key and the diff would never settle (it would spin on loading).
  const nowISO = useMemo(() => (now ?? new Date()).toISOString(), [now]);

  // The "then" side is a chosen date; the "now" side defaults to live ("now").
  // Empty `toDate` ⇒ compare against the frozen "now"; the `toIsNow` flag lets
  // the chrome show "now" on the right (the gallery's default).
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const selection: CompareSelection = {
    from: dateToISO(fromDate) ?? null,
    to: dateToISO(toDate) ?? (fromDate ? nowISO : null),
  };
  const toIsNow = !toDate;

  const { result, isLoading, isError } = useTaskCompare(teamId, selection);

  return (
    <div data-testid="compare-view" className="flex h-[calc(100vh-8rem)] flex-col">
      {/* Chrome: back + the two pickers + the ⇆. Editorial (`V_B35`): cream
          surface, hairline rule, a serif "Compare" title, and the date inputs
          read as mono `<date> ▾ ⇄ now ▾` chips. Legacy: the shipped white bar
          with the indigo "now" pill (byte-identical). */}
      <div
        className="flex flex-wrap items-center gap-3 border-b px-4 py-2"
        style={{
          borderColor: editorial ? c.rule : PALETTE.softBorder,
          backgroundColor: editorial ? c.cardBg : "#FFFFFF",
        }}
      >
        <button
          type="button"
          data-testid="compare-back"
          onClick={onExit}
          className="rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors hover:opacity-90"
          style={{
            borderColor: editorial ? c.rule2 : PALETTE.softBorder,
            color: editorial ? c.ink : PALETTE.slate,
          }}
        >
          ← Map
        </button>
        <span
          className={editorial ? "text-[15px] italic" : "text-[13px] font-bold"}
          style={{
            color: editorial ? c.ink : PALETTE.ink,
            fontFamily: editorial ? theme.fonts.serif : undefined,
          }}
        >
          Compare
        </span>
        <div className="ml-2 flex items-center gap-2">
          <input
            data-testid="compare-from-input"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            aria-label="Compare from"
            className="rounded-md border px-2.5 py-1 text-[11px]"
            style={{
              borderColor: editorial ? c.rule2 : PALETTE.softBorder,
              backgroundColor: editorial ? c.rollupTrack : undefined,
              color: editorial ? c.slate : PALETTE.ink,
              fontFamily: editorial ? theme.fonts.mono : undefined,
            }}
          />
          <span
            aria-hidden
            className="text-sm"
            style={{ color: editorial ? c.muted : PALETTE.teal }}
          >
            ⇆
          </span>
          <input
            data-testid="compare-to-input"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            aria-label="Compare to (blank = now)"
            className="rounded-md border px-2.5 py-1 text-[11px]"
            style={{
              borderColor: editorial ? c.rule2 : toIsNow ? INDIGO : PALETTE.softBorder,
              backgroundColor: editorial ? c.rollupTrack : undefined,
              color: editorial ? c.slate : PALETTE.ink,
              fontFamily: editorial ? theme.fonts.mono : undefined,
            }}
          />
          {toIsNow &&
            (editorial ? (
              <span
                data-testid="compare-now-chip"
                className="rounded-md px-2 py-0.5 text-[11px]"
                style={{
                  backgroundColor: c.rollupTrack,
                  color: c.slate,
                  fontFamily: theme.fonts.mono,
                }}
              >
                now ▾
              </span>
            ) : (
              <span
                className="rounded-md border px-2 py-0.5 text-[11px] font-bold"
                style={{ backgroundColor: "#EEF2FF", borderColor: INDIGO, color: INDIGO }}
              >
                now
              </span>
            ))}
        </div>
      </div>

      {/* The body: a vertical split — the diff list (left) + the summary (right). */}
      <div
        className="flex min-h-0 flex-1"
        style={{ backgroundColor: editorial ? c.canvas : PALETTE.canvas }}
      >
        {isError ? (
          <div
            data-testid="compare-unavailable"
            className="flex h-full w-full flex-col items-center justify-center gap-1 text-center"
            style={{ color: editorial ? c.muted : PALETTE.muted }}
          >
            <div className="text-sm font-semibold">Compare isn’t available yet</div>
            <div className="text-xs" style={{ color: editorial ? c.faint : PALETTE.faint }}>
              It’ll be ready here soon.
            </div>
          </div>
        ) : !selection.from ? (
          <div
            data-testid="compare-pick-prompt"
            className="flex h-full w-full flex-col items-center justify-center gap-1 text-center"
            style={{ color: editorial ? c.muted : PALETTE.muted }}
          >
            <div className="text-sm font-semibold">Pick two points to compare</div>
            <div className="text-xs" style={{ color: editorial ? c.faint : PALETTE.faint }}>
              Choose a “then” date on the left; the right defaults to now.
            </div>
          </div>
        ) : isLoading ? (
          <div
            data-testid="compare-loading"
            className="flex h-full w-full items-center justify-center text-sm"
            style={{ color: editorial ? c.muted : PALETTE.muted }}
          >
            Comparing the plan…
          </div>
        ) : (
          <CompareBody
            result={result}
            fromLabel={instantLabel(result?.from_at ?? selection.from, false)}
            toLabel={instantLabel(result?.to_at, toIsNow)}
          />
        )}
      </div>
    </div>
  );
}

/** The diff list + the focal summary, both off ONE `CompareResult`. */
function CompareBody({
  result,
  fromLabel,
  toLabel,
}: {
  result: CompareResult | null;
  fromLabel: string;
  toLabel: string;
}) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  const tones = diffTones(theme);
  if (!result) return null;
  const nothingChanged =
    result.added_count === 0 && result.changed_count === 0 && result.removed_count === 0;

  const labelFont = editorial ? theme.fonts.mono : undefined;

  return (
    <div className="flex min-h-0 w-full">
      {/* LEFT — the diff list (the "TREE (diff)" panel). */}
      <div
        className="min-h-0 flex-1 overflow-y-auto border-r px-4 py-4"
        style={{ borderColor: editorial ? c.rule : PALETTE.softBorder }}
      >
        <div
          className="mb-2 text-[11px] font-bold tracking-wide"
          style={{ color: editorial ? c.muted : PALETTE.muted, fontFamily: labelFont }}
        >
          THE PLAN ({fromLabel} → {toLabel})
        </div>
        {nothingChanged ? (
          <div className="text-xs italic" style={{ color: editorial ? c.faint : PALETTE.faint }}>
            No structural changes between these two points.
          </div>
        ) : (
          <ul data-testid="compare-diff-list" className="flex flex-col gap-2">
            {result.added.map((n) => (
              <li
                key={`a-${n.id}`}
                data-testid="diff-added"
                className={`flex items-center gap-2 rounded-md border px-3 py-2 text-[12px] ${editorial ? "border-dashed" : ""}`}
                style={{
                  backgroundColor: tones.addedBg,
                  borderColor: tones.addedInk,
                  color: tones.addedInk,
                }}
              >
                <span aria-hidden className="font-bold">
                  +
                </span>
                <span className="truncate font-semibold">{n.title}</span>
                <span className="ml-auto text-[10px] font-bold">NEW</span>
              </li>
            ))}
            {result.changed.map((c2) => (
              <li
                key={`c-${c2.id}`}
                data-testid="diff-changed"
                className="flex items-center gap-2 rounded-md border px-3 py-2 text-[12px]"
                style={{
                  backgroundColor: tones.changedBg,
                  borderColor: tones.changedBorder,
                  color: editorial ? c.ink : PALETTE.ink,
                }}
              >
                <span aria-hidden className="font-bold" style={{ color: tones.changedInk }}>
                  ~
                </span>
                <span className="truncate font-semibold">{c2.title}</span>
                <span className="ml-auto">
                  <ChangeBadges change={c2} tones={tones} />
                </span>
              </li>
            ))}
            {result.removed.map((n) => (
              <li
                key={`r-${n.id}`}
                data-testid="diff-removed"
                className="flex items-center gap-2 rounded-md border border-dashed px-3 py-2 text-[12px]"
                style={{
                  backgroundColor: tones.removedBg,
                  borderColor: tones.removedBorder,
                  color: tones.removedInk,
                }}
              >
                <span aria-hidden className="font-bold">
                  ⊖
                </span>
                <span className="truncate font-semibold line-through">{n.title}</span>
                <span className="ml-auto text-[10px] font-bold">removed</span>
              </li>
            ))}
          </ul>
        )}

        {/* Legend row (gallery-faithful). */}
        <div className="mt-4 flex flex-wrap gap-2 text-[10px]">
          <span
            className={`rounded border px-2 py-0.5 ${editorial ? "border-dashed" : ""}`}
            style={{
              backgroundColor: tones.addedBg,
              borderColor: tones.addedInk,
              color: tones.addedInk,
            }}
          >
            + added
          </span>
          <span
            className="rounded border px-2 py-0.5"
            style={{
              backgroundColor: tones.changedBg,
              borderColor: tones.changedBorder,
              color: tones.changedInk,
            }}
          >
            ~ changed
          </span>
          <span
            className="rounded border border-dashed px-2 py-0.5"
            style={{
              backgroundColor: tones.removedBg,
              borderColor: tones.removedBorder,
              color: tones.removedInk,
            }}
          >
            ⊖ removed
          </span>
        </div>
      </div>

      {/* RIGHT — the focal "WHAT CHANGED" summary. Editorial: a `c.bgRecess`
          rail, a mono "WHAT CHANGED" label, group rows tinted to match the
          diff, and a `<dot> → <dot>` state transition on changed rows. */}
      <div
        className="min-h-0 w-[42%] shrink-0 overflow-y-auto px-4 py-4"
        style={{ backgroundColor: editorial ? c.bgRecess : undefined }}
      >
        <div
          className="mb-2 text-[11px] font-bold tracking-wide"
          style={{ color: editorial ? c.muted : PALETTE.muted, fontFamily: labelFont }}
        >
          WHAT CHANGED
        </div>
        <div className="flex flex-col gap-3">
          {/* Added. */}
          <section
            data-testid="summary-added"
            className="rounded-lg border p-3"
            style={{
              backgroundColor: editorial ? c.cardBg : CHANGED_BG,
              borderColor: editorial ? c.rule : ADDED_BG,
            }}
          >
            <div className="text-[12px] font-bold" style={{ color: tones.addedInk }}>
              + Added (<span data-testid="added-count">{result.added_count}</span>)
            </div>
            <ul className="mt-2 flex flex-col gap-1">
              {result.added.length === 0 ? (
                <li
                  className="text-[11px] italic"
                  style={{ color: editorial ? c.faint : PALETTE.faint }}
                >
                  nothing added in this window
                </li>
              ) : (
                result.added.map((n) => (
                  <li
                    key={n.id}
                    className={`flex items-center gap-2 text-[12px] ${editorial ? "rounded px-2 py-1" : ""}`}
                    style={{
                      backgroundColor: editorial ? tones.addedBg : undefined,
                      color: tones.addedInk,
                    }}
                  >
                    <span style={{ fontFamily: labelFont }}>+</span> {n.title}
                  </li>
                ))
              )}
            </ul>
          </section>

          {/* Changed. */}
          <section
            data-testid="summary-changed"
            className="rounded-lg border p-3"
            style={{
              backgroundColor: editorial ? c.cardBg : CHANGED_BG,
              borderColor: editorial ? c.rule : ADDED_BG,
            }}
          >
            <div className="text-[12px] font-bold" style={{ color: tones.changedInk }}>
              ~ Changed (<span data-testid="changed-count">{result.changed_count}</span>)
            </div>
            <ul className="mt-2 flex flex-col gap-1.5">
              {result.changed.length === 0 ? (
                <li
                  className="text-[11px] italic"
                  style={{ color: editorial ? c.faint : PALETTE.faint }}
                >
                  nothing changed in this window
                </li>
              ) : (
                result.changed.map((c2) => (
                  <li
                    key={c2.id}
                    className={`flex items-center justify-between gap-2 text-[12px] ${editorial ? "rounded px-2 py-1" : ""}`}
                    style={{
                      backgroundColor: editorial ? tones.changedBg : undefined,
                      color: editorial ? c.ink : PALETTE.ink,
                    }}
                  >
                    <span className="truncate">
                      <span style={{ fontFamily: labelFont }}>~</span> {c2.title}
                    </span>
                    {editorial ? (
                      <StateTransitionDots change={c2} />
                    ) : (
                      <ChangeBadges change={c2} tones={tones} />
                    )}
                  </li>
                ))
              )}
            </ul>
          </section>

          {/* Removed. */}
          <section
            data-testid="summary-removed"
            className="rounded-lg border p-3"
            style={{
              backgroundColor: editorial ? c.cardBg : "#F7F8FA",
              borderColor: editorial ? c.rule : PALETTE.cardBorder,
            }}
          >
            <div className="text-[12px] font-bold" style={{ color: tones.removedInk }}>
              ⊖ Removed (<span data-testid="removed-count">{result.removed_count}</span>)
            </div>
            <ul className="mt-2 flex flex-col gap-1">
              {result.removed.length === 0 ? (
                <li className="text-[11px] italic" style={{ color: tones.removedInk }}>
                  nothing removed in this window
                </li>
              ) : (
                result.removed.map((n) => (
                  <li
                    key={n.id}
                    className={`flex items-center gap-2 text-[12px] ${editorial ? "rounded px-2 py-1" : ""}`}
                    style={{
                      backgroundColor: editorial ? tones.removedBg : undefined,
                      color: tones.removedInk,
                    }}
                  >
                    <span style={{ fontFamily: labelFont }}>⊖</span> {n.title}
                  </li>
                ))
              )}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

// Re-exported for the (rare) caller that wants the pill in isolation; keeps the
// component surface discoverable without widening the module's public API.
export { StatePill };
